/**
 * Homepage people spotlight — pages through the person cards two at a time on
 * desktop and one on mobile.
 *
 * The track is a scroll-snap scroller, so how many cards fit on a page is a
 * CSS decision. Everything here measures it rather than repeating the
 * breakpoint, which means the layout can change without touching this file.
 * Without JS the track is still scrollable; the controls stay hidden.
 *
 * No-ops when .js-people-spotlight is absent.
 */

export default function peopleSpotlight() {
  const root = document.querySelector('.js-people-spotlight');
  if (!root) return;

  const track = root.querySelector('.js-spotlight-track');
  const pager = root.querySelector('.js-spotlight-pager');
  if (!track || !pager) return;

  const cards = Array.from(track.querySelectorAll('.personcard'));
  if (cards.length < 2) return;

  const dotsBox = pager.querySelector('.js-spotlight-dots');
  const prev = pager.querySelector('.js-spotlight-prev');
  const next = pager.querySelector('.js-spotlight-next');
  const live = root.querySelector('.js-spotlight-live');

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduce ? 'auto' : 'smooth';

  let perPage = 1;
  let pages = 1;
  let dots = [];

  // Card width plus the gap, straight off the layout.
  function step() {
    return cards[1].offsetLeft - cards[0].offsetLeft || cards[0].offsetWidth;
  }

  function offsetOf(page) {
    const card = cards[Math.min(cards.length - 1, page * perPage)];
    return card.offsetLeft - cards[0].offsetLeft;
  }

  function currentPage() {
    return Math.max(0, Math.min(pages - 1, Math.round(track.scrollLeft / (step() * perPage))));
  }

  function goTo(page, announce) {
    track.scrollTo({ left: offsetOf(page), behavior });
    if (announce) say(page);
  }

  function say(page) {
    if (!live) return;
    const first = page * perPage + 1;
    const last = Math.min(cards.length, first + perPage - 1);
    live.textContent = first === last
      ? `Showing ${first} of ${cards.length}`
      : `Showing ${first} to ${last} of ${cards.length}`;
  }

  function sync() {
    const page = currentPage();
    dots.forEach((dot, i) => {
      const on = i === page;
      dot.classList.toggle('is-active', on);
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    // No wrapping: the track can also be dragged, and a silent jump back to
    // the start mid-drag reads as a glitch.
    if (prev) prev.disabled = page === 0;
    if (next) next.disabled = page === pages - 1;
  }

  function buildDots() {
    if (!dotsBox) return;
    dotsBox.textContent = '';
    dots = [];
    for (let i = 0; i < pages; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'peoplefeature__dot';
      dot.setAttribute('aria-label', `Show people ${i * perPage + 1} to ${Math.min(cards.length, (i + 1) * perPage)}`);
      dot.addEventListener('click', () => goTo(i, true));
      dotsBox.appendChild(dot);
      dots.push(dot);
    }
  }

  function measure() {
    perPage = Math.max(1, Math.round(track.clientWidth / step()));
    pages = Math.ceil(cards.length / perPage);
    pager.hidden = pages < 2;
    buildDots();
    sync();
  }

  if (prev) prev.addEventListener('click', () => goTo(currentPage() - 1, true));
  if (next) next.addEventListener('click', () => goTo(currentPage() + 1, true));

  let ticking = false;
  track.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;
      sync();
    });
  }, { passive: true });

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measure, 150);
  });

  measure();
  // Cards are sized by flex-basis rather than by their contents, so the first
  // measure is right long before the portraits arrive. Re-measure anyway, in
  // case the stylesheet hadn't applied yet.
  window.addEventListener('load', measure);
}
